import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { HeatLinkButton } from "@/shared/data-entries/HeatLinkButton";
import { RefreshButtons } from "@/shared/data-entries/RefreshButtons";
import { StatusTitle } from "@/shared/data-entries/StatusTitle";
import { NotesFieldDE } from "@/shared/data-entries/common/NotesFieldDE";
import { ProcessDataCheckboxFieldDE } from "@/shared/data-entries/process-data/ProcessDataCheckboxField";
import { ProcessDataDateTimeFieldDE } from "@/shared/data-entries/process-data/ProcessDataDateTimeFieldDE";
import { ProcessDataNumberTextField } from "@/shared/data-entries/process-data/ProcessDataNumberTextField";
import { ProcessDataTextFieldDE } from "@/shared/data-entries/process-data/ProcessDataTextFieldDE";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { LadleSelector } from "@/shared/selectors/LadleSelector";
import { OperatorSelector } from "@/shared/selectors/OperatorSelector";
import {
  formatProductionDate,
  secondsToDate,
} from "@/utils/date-time-formatter";
import DurationInput from "@/shared/data-entries/common/DurationInput";
import { mmSsToDecimalMinutes } from "@/utils/durationHelpers";
import {
  ErrorModal,
  HelmetDexteel,
  dxtToLocalServerTime,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { get, isNil } from "lodash-es";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { TableChemistry } from "../../laboratory/data-entry/components/tables/chemistries/TableChemistry";
import { ProcessNavigator } from "@/shared/data-entries/ProcessNavigator";
import {
  getLastestProcesses,
  searchProcesses,
} from "./repositories/eaf.repositories";
import { LinkEAFProcess } from "./components/modals/LinkEAFDE";
import { MissingFieldsBanner } from "./components/MissingFieldsBanner";
import { useTheme } from "@mui/material/styles";
import { eafDETablesStyles } from "./components/tables/styles/eafDETablesStyles";
import { TableAdditions } from "./components/tables/table-additions/TableAdditions";
import { TableCharges } from "./components/tables/table-charges/TableCharges";
import { TableHeatings } from "./components/tables/table-heatings/TableHeatings";
import { TableInjection } from "./components/tables/table-injection/TableInjection";
import { TablePhases } from "./components/tables/table-phases/TablePhases";
import { TableProbes } from "./components/tables/table-probes/TableProbes";
import { TableSlag } from "./components/tables/table-slag/TableSlag";
import { useDataEntryContext } from "./context/reducer.provider";
import { useTimerContext } from "./context/timer.provider";
import { validateTimeField } from "./helpers/de-helper";
import { useEAFDataEntryActions } from "./hooks/useEAFDataEntryActions";
import { useLinkUnlinkProcess } from "./hooks/useLinkUnlinkProcess";
import { Process } from "./models/eaf.models";
import { dataEntryStyles } from "../../../shared/styles/dataEntryStyles";

type FormValues = {
  StartHeat: Date | null;
  StartTap: Date | null;
  End: Date | null;
};

export const EafDataEntryPage = () => {
  const theme = useTheme();
  const styles = dataEntryStyles(theme);
  const tableStyles = eafDETablesStyles(theme);

  const { state } = useUTLSettingsContext();
  const targetTTT = parseInt(get(state.settings, "EAF_TARGET_TTT", 60));

  const navigate = useNavigate();
  const { seconds, isRunning, pause, restart, resume } = useTimerContext();

  const { saveHeat, saveProcessData, saveProcessEnd, refreshData, loadData } =
    useEAFDataEntryActions();
  const params = useParams();
  const {
    state: {
      deProcess,
      notificationMessage,
      isDataLoading,
      errorMessage,
      lastUpdate,
    },
    actions: { setProcess, setNotificationMessage, setErrorMessage },
  } = useDataEntryContext();
  const process = deProcess?.process;
  const missingFieldNames = new Set(
    deProcess?.missingFields?.map((f) => f.FieldName) ?? [],
  );

  const { control, reset, setValue } = useForm<FormValues>({
    mode: "onChange",
  });

  const [showClearEndModal, setShowClearEndModal] = useState(false);
  const [nonFreeOpenConfirm, setNonFreeOpenConfirm] = useState({
    show: false,
    value: false,
  });

  const {
    handleLinkHeat,
    handleUnlinkHeat,
    showLinkModal,
    showUnlinkModal,
    unlinkProcess,
    setShowLinkModal,
  } = useLinkUnlinkProcess(process || ({} as Process));

  useEffect(() => {
    if (process) {
      reset({
        StartHeat: process.StartHeat,
        StartTap: process.StartTap,
        End: process.End,
      });
    }
  }, [process?.ProcessId]);

  return (
    <>
      <HelmetDexteel
        title={`${
          process?.HeatName ? `${process.HeatName} - ` : ""
        }EAF Data Entry`}
      />
      {deProcess?.missingFields && deProcess.missingFields.length > 0 && (
        <MissingFieldsBanner missingFields={deProcess.missingFields} />
      )}
      <Box padding={2} style={{ height: "calc(100vh - 64px)" }}>
        <Grid container spacing={1} direction={"column"}>
          <Grid item>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              EAF Data Entry
            </Typography>
          </Grid>
          <Grid item>
            <Paper>
              <Box padding={1}>
                <Grid
                  container
                  justifyContent={"space-between"}
                  alignItems={"flex-start"}
                  spacing={3}
                >
                  <Grid item xs={12} md={2}>
                    <Grid container direction="column" spacing={1}>
                      <Grid item>
                        <ProcessNavigator
                          key={`eaf-de-process-navigator`}
                          process={deProcess?.process ?? null}
                          loadData={loadData}
                          getLatestProcesses={getLastestProcesses}
                          searchProcesses={searchProcesses}
                          pause={pause}
                          resume={resume}
                          onError={setErrorMessage}
                          initialProcessId={
                            params.processId
                              ? Number(params.processId)
                              : undefined
                          }
                        />
                      </Grid>
                      <Grid item>
                        <Grid
                          container
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Grid item md={10}>
                            <StatusTitle
                              heatStatus={process?.HeatStatusCode ?? ""}
                            />
                          </Grid>
                          <Grid item md={2}>
                            <HeatLinkButton
                              isLinked={process?.HeatId != null}
                              disabled={!process}
                              onLink={() => {
                                pause();
                                if (process) {
                                  handleLinkHeat();
                                }
                              }}
                              onUnlink={async () => {
                                pause();
                                if (process) {
                                  await unlinkProcess();
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    key={`eaf-data-entry-first-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid container direction={"column"} spacing={1}>
                      <Grid item>
                        <TextField
                          fullWidth
                          label={"Shift"}
                          variant={"outlined"}
                          disabled
                          value={`${formatProductionDate(process?.ProductionDate)} ${process?.Shift} - ${process?.Crew}`}
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          fullWidth
                          label={"Start"}
                          variant={"outlined"}
                          disabled
                          value={`${dxtToLocalServerTime(
                            process?.Start,
                            "MM-dd-yyyy HH:mm",
                          )}`}
                        />
                      </Grid>
                      <Grid item>
                        <Controller
                          name="StartHeat"
                          control={control}
                          rules={{
                            validate: (value) =>
                              validateTimeField(value, "StartHeat", process),
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <ProcessDataDateTimeFieldDE<Process>
                                value={field.value}
                                field="StartHeat"
                                label="Start Heat"
                                format="MM-DD-YYYY HH:mm"
                                clearable={false}
                                onChange={(value) => {
                                  field.onChange(value);
                                  setProcess({ ...process, StartHeat: value });
                                }}
                                onBlur={(value) => {
                                  if (!error) {
                                    saveProcessData({
                                      processId: process?.ProcessId ?? 0,
                                      field: "StartHeat",
                                      value: value,
                                    }).then(() => resume());
                                  }
                                }}
                                onFocus={pause}
                              />
                              {error && (
                                <Typography variant="caption" color="error">
                                  {error.message}
                                </Typography>
                              )}
                            </>
                          )}
                        />
                      </Grid>
                      <Grid item>
                        <Controller
                          name="StartTap"
                          control={control}
                          rules={{
                            validate: (value) =>
                              validateTimeField(value, "StartTap", process),
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <ProcessDataDateTimeFieldDE<Process>
                                value={field.value}
                                field={"StartTap"}
                                label="Start Tap"
                                format="MM-DD-YYYY HH:mm"
                                clearable={false}
                                onChange={(value) => {
                                  field.onChange(value);
                                  setProcess({ ...process, StartTap: value });
                                }}
                                onBlur={(value) => {
                                  if (!error) {
                                    saveProcessData({
                                      processId: process?.ProcessId ?? 0,
                                      field: "StartTap",
                                      value: value,
                                    }).then(() => resume());
                                  }
                                }}
                                onFocus={pause}
                              />
                              {error && (
                                <Typography variant="caption" color="error">
                                  {error.message}
                                </Typography>
                              )}
                            </>
                          )}
                        />
                      </Grid>
                      <Grid item>
                        <Controller
                          name="End"
                          control={control}
                          rules={{
                            validate: (value) =>
                              validateTimeField(value, "End", process),
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <>
                              <ProcessDataDateTimeFieldDE<Process>
                                value={field.value}
                                field="End"
                                label="End"
                                format="MM-DD-YYYY HH:mm"
                                clearable={false}
                                skipBlurOnClear={!!process?.IsOldProcess}
                                onChange={(value) => {
                                  if (value === null && process?.IsOldProcess) {
                                    setShowClearEndModal(true);
                                  } else {
                                    field.onChange(value);
                                    setProcess({ ...process, End: value });
                                  }
                                }}
                                onBlur={(value) => {
                                  if (showClearEndModal) return;
                                  if (!error) {
                                    const updatedProcessForSave = {
                                      ...process,
                                      End: value,
                                      AssetId: process?.AssetId ?? 0,
                                      ProcessId: process?.ProcessId ?? 0,
                                    };

                                    saveProcessEnd(
                                      updatedProcessForSave as Process,
                                    ).then(() => resume());
                                  }
                                }}
                                onFocus={pause}
                              />
                              {error && (
                                <Typography variant="caption" color="error">
                                  {error.message}
                                </Typography>
                              )}
                            </>
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    key={`eaf-data-entry-sec-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid container direction={"column"} spacing={1}>
                      <Grid item>
                        <LadleSelector
                          disableClearable
                          disabled={process?.HeatId ? false : true}
                          value={process?.LadleId || null}
                          highlight={missingFieldNames.has("LadleId")}
                          onChange={(value: number) => {
                            saveHeat({
                              heatId: process?.HeatId ?? 0,
                              field: "LadleId",
                              value,
                            });
                          }}
                          onOpen={pause}
                          onClose={resume}
                        />
                      </Grid>
                      <Grid item>
                        <GradeSelector
                          disableClearable
                          disabled={!process?.HeatId}
                          value={process?.GradeId ?? null}
                          highlight={missingFieldNames.has("GradeId")}
                          onChange={(value: number | null) => {
                            if (process?.HeatId) {
                              saveHeat({
                                heatId: process.HeatId,
                                field: "GradeId",
                                value,
                              });
                            }
                          }}
                          onFocus={pause}
                          onBlur={resume}
                        />
                      </Grid>
                      <Grid item>
                        <OperatorSelector
                          disableClearable
                          value={process?.OperatorForemanId || null}
                          assetId={process?.AssetId ?? 0}
                          onChange={(value: number | null) => {
                            saveProcessData({
                              processId: process?.ProcessId
                                ? process?.ProcessId
                                : 0,
                              field: "OperatorForemanId",
                              value,
                            });
                          }}
                          label={"Foreman"}
                          onlyForemans={true}
                          onFocus={pause}
                          onBlur={resume}
                        />
                      </Grid>
                      <Grid item>
                        <ProcessDataTextFieldDE<Process>
                          field={"ForemanFillInName"}
                          label={"Fill In"}
                          value={process?.ForemanFillInName}
                          onChange={(value) => {
                            setProcess({
                              ...process,
                              ["ForemanFillInName"]: value,
                            });
                          }}
                          onBlur={() => {
                            saveProcessData({
                              processId: process?.ProcessId!,
                              field: "ForemanFillInName",
                              value: process!["ForemanFillInName"],
                            }).then(() => {
                              resume();
                            });
                          }}
                          onFocus={pause}
                        />
                      </Grid>
                      <Grid item>
                        <OperatorSelector
                          disableClearable
                          value={process?.OperatorId || null}
                          assetId={process?.AssetId ?? 0}
                          onChange={(value: number | null) => {
                            saveProcessData({
                              processId: process?.ProcessId
                                ? process?.ProcessId
                                : 0,
                              field: "OperatorId",
                              value,
                            });
                          }}
                          label={"Operator"}
                          onFocus={pause}
                          onBlur={resume}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    key={`eaf-data-entry-third-fourth${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={5}
                  >
                    <Grid
                      container
                      direction="row"
                      justifyContent={"space-between"}
                      spacing={3}
                    >
                      <Grid
                        key={`eaf-data-entry-third-${process?.ProcessId}`}
                        item
                        xs={12}
                        sm={6}
                        md={6}
                      >
                        <Grid container direction={"column"} spacing={1}>
                          <Grid item container spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <TextField
                                fullWidth
                                label={"Tap To Tap [min]"}
                                variant={"outlined"}
                                disabled
                                value={
                                  process?.TapToTap
                                    ? process?.TapToTap.toFixed(1)
                                    : 0
                                }
                                onChange={(e) => {
                                  setProcess({
                                    ...process,
                                    TapToTap: Number(e.target.value),
                                  });
                                }}
                                slotProps={{
                                  input: {
                                    sx: {
                                      color:
                                        targetTTT &&
                                        (process?.TapToTap ?? 0) > targetTTT
                                          ? "red"
                                          : "inherit",
                                    },
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <ProcessDataNumberTextField<Process>
                                field={"SteelWeight"}
                                label={"Steel Weight [t]"}
                                value={
                                  process?.SteelWeight
                                    ? parseFloat(process.SteelWeight.toFixed(1))
                                    : null
                                }
                                disabled={process?.HeatId ? false : true}
                                onChange={(e) => {
                                  setProcess({
                                    ...process,
                                    SteelWeight: e,
                                  });
                                }}
                                onBlur={() => {
                                  saveHeat({
                                    heatId: process?.HeatId ?? 0,
                                    field: "SteelWeight",
                                    value: process?.SteelWeight,
                                  }).then(() => {
                                    resume();
                                  });
                                }}
                                onFocus={pause}
                              />
                            </Grid>
                          </Grid>
                          <Grid item container spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <DurationInput
                                disabled={process?.HeatStatusCode === "M"}
                                label="Power On [mm:ss]"
                                mode="mm:ss"
                                initMinutes={
                                  !isNil(process?.PowerOn)
                                    ? (process.PowerOn as number) / 60
                                    : 0
                                }
                                onTimeChange={(time) => {
                                  const totalSeconds = Math.round(
                                    mmSsToDecimalMinutes(time) * 60,
                                  );
                                  setProcess({
                                    ...process,
                                    PowerOn: totalSeconds,
                                  });
                                  saveProcessData({
                                    processId: process?.ProcessId ?? 0,
                                    field: "PowerOn",
                                    value: totalSeconds,
                                  }).then(resume);
                                }}
                                onFocusHandler={pause}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <DurationInput
                                disabled
                                label="Power Off [mm:ss]"
                                mode="mm:ss"
                                initMinutes={
                                  !isNil(process?.PowerOff)
                                    ? (process.PowerOff as number) / 60
                                    : 0
                                }
                              />
                            </Grid>
                          </Grid>
                          <Grid item container spacing={1}>
                            <Grid item xs={12} sm={6}>
                              <ProcessDataNumberTextField<Process>
                                field={"MWH"}
                                label={"Energy [mwh]"}
                                value={process?.MWH}
                                onChange={(value) => {
                                  setProcess({
                                    ...process,
                                    ["MWH"]: value,
                                  });
                                }}
                                onBlur={() => {
                                  saveProcessData({
                                    processId: process?.ProcessId ?? 0,
                                    field: "MWH",
                                    value: process!["MWH"],
                                  }).then(() => {
                                    resume();
                                  });
                                }}
                                onFocus={pause}
                              />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <ProcessDataNumberTextField<Process>
                                field={"KWHPerTon"}
                                label={"Kwh / t"}
                                value={
                                  process?.KWHPerTon
                                    ? parseFloat(process.KWHPerTon.toFixed(1))
                                    : null
                                }
                                onChange={(value) => {
                                  setProcess({
                                    ...process,
                                    KWHPerTon: Number(value),
                                  });
                                }}
                                onBlur={() => {
                                  saveProcessData({
                                    processId: process?.ProcessId ?? 0,
                                    field: "KWHPerTon",
                                    value: process?.KWHPerTon
                                      ? process?.KWHPerTon
                                      : 0,
                                  }).then(() => {
                                    resume();
                                  });
                                }}
                                onFocus={pause}
                              />
                            </Grid>
                          </Grid>
                          <Grid
                            key={`eaf-data-entry-notes-${process?.ProcessId}`}
                            item
                            xs={12}
                            md={6}
                            sx={{ mt: 1 }}
                          >
                            <NotesFieldDE
                              value={process?.Comments ?? ""}
                              minRows={4}
                              maxRows={4}
                              onChange={(value) => {
                                setProcess({
                                  ...process,
                                  Comments: value,
                                });
                              }}
                              onBlur={() => {
                                saveProcessData({
                                  processId: process?.ProcessId ?? 0,
                                  field: "Comments",
                                  value: process?.Comments ?? "",
                                }).then(() => {
                                  resume();
                                });
                              }}
                              onFocus={pause}
                            />
                          </Grid>
                        </Grid>
                      </Grid>

                      <Grid
                        key={`eaf-data-entry-forth-${process?.ProcessId}`}
                        item
                        xs={12}
                        md={5}
                      >
                        <Grid container direction={"column"} spacing={2}>
                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <RefreshButtons
                              seconds={seconds}
                              isRunning={isRunning}
                              pause={pause}
                              restart={restart}
                              resume={resume}
                              refreshData={refreshData}
                              isDataLoading={isDataLoading}
                              process={process}
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ mt: 0.5 }}>
                            <Button
                              fullWidth
                              variant={"outlined"}
                              size="large"
                              onClick={() =>
                                navigate(
                                  `/eaf-processes/${process?.ShiftId}?processId=${process?.ProcessId}`,
                                )
                              }
                              sx={styles.buttons}
                            >
                              Processes
                            </Button>
                          </Grid>
                          <Grid item xs={12} sx={{ mt: 0.5 }}>
                            <Button
                              fullWidth
                              variant={"outlined"}
                              size="large"
                              onClick={() =>
                                navigate(
                                  `/meltshop-heats/${process?.ShiftId}?heatName=${process?.HeatName}`,
                                )
                              }
                              sx={styles.buttons}
                            >
                              Heats List
                            </Button>
                          </Grid>
                          <Grid item xs={12}>
                            <Tooltip
                              title={
                                process?.IsNonFreeOpenModifiedBy &&
                                !nonFreeOpenConfirm.show
                                  ? `Last changed by ${
                                      process.IsNonFreeOpenModifiedBy
                                    } on ${dxtToLocalServerTime(
                                      process.IsNonFreeOpenModifiedAt,
                                      "MM/dd/yyyy 'at' HH:mm",
                                    )}`
                                  : ""
                              }
                              arrow
                              placement="right"
                              disableFocusListener
                            >
                              <span>
                                <ProcessDataCheckboxFieldDE<Process>
                                  field={"IsNonFreeOpen"}
                                  label="Non-Free Opening"
                                  value={process?.IsNonFreeOpen}
                                  onChange={(value) => {
                                    setNonFreeOpenConfirm({
                                      show: true,
                                      value,
                                    });
                                  }}
                                  disabled={isDataLoading}
                                />
                              </span>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>
          <Grid item container spacing={1}>
            <Grid item xs={12} md={4}>
              <Grid container direction={"column"} spacing={1}>
                <Grid item xs={12} md={12}>
                  <TableProbes
                    key={`eaf-de-table-probes-${process?.ProcessId}`}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <TableAdditions
                    key={`eaf-de-table-additions-${process?.ProcessId}`}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container direction={"column"} spacing={1}>
                <Grid item xs={12} md={12}>
                  <TablePhases />
                </Grid>
                <Grid item xs={12} md={12}>
                  <TableHeatings
                    key={`eaf-de-table-heatings-${process?.ProcessId}`}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container direction={"column"} spacing={1}>
                <Grid item xs={12} md={12}>
                  <TableCharges />
                </Grid>
                <Grid item xs={12} md={12}>
                  <TableInjection />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={12}>
            <Paper>
              <Box padding={1} sx={tableStyles.smHorizontalPaper}>
                <Grid container spacing={3}>
                  {/* Column 1 */}
                  <Grid item xs={12} sm={2} md={2}>
                    <Grid container spacing={1} direction={"column"}>
                      <Grid item>
                        <ProcessDataNumberTextField<Process>
                          field={"Heel"}
                          value={process?.Heel}
                          onChange={(value) => {
                            setProcess({
                              ...process,
                              ["Heel"]: value,
                            });
                          }}
                          onBlur={() => {
                            saveProcessData({
                              processId: process?.ProcessId ?? 0,
                              field: "Heel",
                              value: process!["Heel"],
                            }).then(() => {
                              resume();
                            });
                          }}
                          onFocus={pause}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Column 2 */}
                  <Grid item xs={12} sm={2} md={2}>
                    <Grid container spacing={1} direction={"column"}>
                      <Grid item>
                        <ProcessDataNumberTextField<Process>
                          field={"Lime"}
                          value={process?.Lime}
                          onChange={(value) => {
                            setProcess({
                              ...process,
                              ["Lime"]: value,
                            });
                          }}
                          onBlur={() => {
                            saveProcessData({
                              processId: process?.ProcessId ?? 0,
                              field: "Lime",
                              value: process!["Lime"],
                            }).then(() => {
                              resume();
                            });
                          }}
                          onFocus={pause}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Column 3 */}
                  <Grid item xs={12} sm={4} md={4}>
                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <ProcessDataCheckboxFieldDE<Process>
                          field={"Electrode1"}
                          label="Electrode 1"
                          value={process?.Electrode1}
                          onChange={(value) => {
                            saveProcessData({
                              processId: process?.ProcessId ?? 0,
                              field: "Electrode1",
                              value: value,
                            });
                          }}
                          disabled={isDataLoading}
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <ProcessDataCheckboxFieldDE<Process>
                          field={"Electrode2"}
                          label="Electrode 2"
                          value={process?.Electrode2}
                          onChange={(value) => {
                            saveProcessData({
                              processId: process?.ProcessId ?? 0,
                              field: "Electrode2",
                              value: value,
                            });
                          }}
                          disabled={isDataLoading}
                        />
                      </Grid>

                      <Grid item xs={4}>
                        <ProcessDataCheckboxFieldDE<Process>
                          field={"Electrode3"}
                          label="Electrode 3"
                          value={process?.Electrode3}
                          onChange={(value) => {
                            saveProcessData({
                              processId: process?.ProcessId ?? 0,
                              field: "Electrode3",
                              value: value,
                            });
                          }}
                          disabled={isDataLoading}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Column 5 */}
                  <Grid item xs={12} sm={2} md={2}>
                    <Grid container spacing={1} direction={"column"}>
                      <Grid item>
                        <ProcessDataNumberTextField<Process>
                          disabled
                          field={"InjectedCarbon"}
                          label="Total Carbon Inj"
                          value={process?.InjectedCarbon}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Column 6 */}
                  <Grid item xs={12} sm={2} md={2}>
                    <Grid container spacing={1} direction={"column"}>
                      <Grid item>
                        <ProcessDataNumberTextField<Process>
                          disabled
                          field={"InjectedO2"}
                          label="Total O2 Inj"
                          value={
                            process?.InjectedO2
                              ? parseFloat(process.InjectedO2.toFixed(2))
                              : null
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={12}>
            <TableChemistry
              key={`eaf-data-entry-${deProcess?.process.HeatId}`}
              heatId={deProcess?.process?.HeatId ?? null}
            />
          </Grid>

          {(deProcess?.slags?.length ?? 0) > 0 && (
            <Grid item xs={12} md={12}>
              <TableSlag key={`eaf-de-table-slag-${process?.ProcessId}`} />
            </Grid>
          )}
        </Grid>
      </Box>
      <ConfirmationDialogRaw
        title="Confirmation"
        message="By removing ‘End Tapping’, the next process will be deleted, and this process will be set as the final heat in the sequence."
        open={showClearEndModal}
        onClose={(confirmed) => {
          setShowClearEndModal(false);

          if (confirmed === "OK") {
            setValue("End", null);
            setProcess({ ...process, End: null });
            const updatedProcessForSave = {
              ...process,
              End: null,
              AssetId: process?.AssetId ?? 0,
              ProcessId: process?.ProcessId ?? 0,
            };
            saveProcessEnd(updatedProcessForSave as Process).then(() =>
              resume(),
            );
          }
        }}
      />
      <ConfirmationDialogRaw
        title="Confirmation"
        message={showUnlinkModal.message}
        open={showUnlinkModal.show}
        onClose={handleUnlinkHeat}
      />
      <ConfirmationDialogRaw
        title="Confirm Opening Mode"
        message={`Mark heat ${process?.HeatName ?? ""} as a ${
          nonFreeOpenConfirm.value ? "non-free" : "free"
        } opening?`}
        confirmText="Confirm"
        open={nonFreeOpenConfirm.show}
        onClose={(confirmed) => {
          setNonFreeOpenConfirm({ ...nonFreeOpenConfirm, show: false });
          if (confirmed === "OK") {
            saveProcessData({
              processId: process?.ProcessId ?? 0,
              field: "IsNonFreeOpen",
              value: nonFreeOpenConfirm.value,
            });
          }
        }}
      />
      <LinkEAFProcess
        show={showLinkModal}
        onHide={() => {
          setShowLinkModal(false);
          resume();
        }}
      />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={errorMessage}
        onHide={() => setErrorMessage("")}
        title="Unexpected error"
      />
    </>
  );
};

export default EafDataEntryPage;
